import { Suspense, lazy, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import { useDispatch, useSelector } from 'store';
import { datLai, getCapNhatThongTinHienTai, moDialog } from 'store/slices/capNhatThongTin';
import FeatureErrorBoundary from 'ui-component/FeatureErrorBoundary';

// Tách khỏi bundle khởi động: người không cần tới thì không tải.
const CapNhatThongTinDialog = lazy(() => import('./CapNhatThongTinDialog'));

// Danh sách TRẮNG các vai trò được kê khai — cùng luật với `duocDangKy` của đợt lưu trú.
// Dùng danh sách trắng thay vì danh sách đen vì FirebaseContext có nhánh cho ra
// role === undefined (tài khoản isSupport không có mã hạnh sinh), giá trị đó lọt qua
// danh sách đen.
const VAI_TRO_DUOC_KE_KHAI = ['HANHSINH', 'HSDD'];

// Bỏ dấu `/` thừa ở cuối trước khi so đường dẫn. Trang chủ là '/' nên sau khi bỏ thì thành
// chuỗi rỗng — đó là giá trị cần so.
const chuanHoaDuongDan = (duongDan) => String(duongDan || '').replace(/\/+$/, '');

const O_TRANG_CHU = (duongDan) => chuanHoaDuongDan(duongDan) === '';

// Nguồn sự thật DUY NHẤT cho câu hỏi "tài khoản này được kê khai không?".
// Gate dùng để quyết định có chạy gì không; trang cập nhật dùng để giải thích cho người
// bị loại, thay vì treo "Đang tải…" vĩnh viễn do không ai gọi API hộ họ.
export const duocKeKhai = (user) => Boolean(user) && Boolean(user.maHs) && VAI_TRO_DUOC_KE_KHAI.includes(user.role);

// Toàn bộ logic tính năng nằm ở đây, và component này luôn được render BÊN TRONG
// ErrorBoundary. Boundary là con thì không bắt được lỗi do cha ném ra, nên mọi hook
// có thể ném (selector, effect) phải nằm dưới boundary một tầng.
const CapNhatThongTinGateHanhSinh = ({ maHs }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const daMoLanNay = useRef(false);

    const { dot, phieu, canEdit } = useSelector((state) => state.capNhatThongTin);

    // Phụ thuộc maHs, KHÔNG chốt theo `loaded`: đổi tài khoản trong cùng tab thì nạp lại đúng
    // dữ liệu người mới, và một lần lỗi mạng không dính cả phiên. MainLayout không remount khi
    // chuyển route con nên đây vẫn là một request mỗi phiên đăng nhập.
    useEffect(() => {
        // Xoá sạch dữ liệu người trước TRƯỚC khi tải dữ liệu người mới: giữa lúc đăng nhập và
        // lúc API trả về (mạng yếu, cộng getIdToken) có thể vài giây — không ai được nhìn thấy
        // phiếu của người khác dù chỉ một nhịp.
        dispatch(datLai());
        dispatch(getCapNhatThongTinHienTai());
    }, [dispatch, maHs]);

    // Nhắc MỖI LẦN hạnh sinh về trang chủ, cho tới khi gửi xong — cố ý không ghi nhớ "Để sau"
    // ở trình duyệt. Gửi rồi thì `phieu.daGui` là true nên không bao giờ nhắc nữa.
    //
    // CHỐT THEO `daGui` CHỨ KHÔNG PHẢI `phieu !== null`: khác hẳn đợt lưu trú. Phiếu ở đây
    // được tạo sẵn cho toàn bộ hạnh sinh nên `phieu` luôn khác null ngay từ đầu — lấy nó làm
    // điều kiện thì popup không bao giờ hiện cho một ai.
    //
    // `daMoLanNay` được ĐẶT LẠI ngay khi rời trang chủ, nên lần sau quay về là nhắc tiếp.
    // Trong cùng một lần ở trang chủ thì nó chặn việc bật lại: bấm "Để sau" xong mà store
    // đổi (tải lại dữ liệu chẳng hạn) cũng không dựng popup lên trước mũi người ta.
    useEffect(() => {
        if (!O_TRANG_CHU(location.pathname)) {
            daMoLanNay.current = false;
            return;
        }

        if (!dot || !canEdit || !phieu || phieu.daGui) return;
        if (daMoLanNay.current) return;

        daMoLanNay.current = true;
        dispatch(moDialog());
    }, [dispatch, dot, phieu, canEdit, location.pathname]);

    // Đợt đang tắt thì Dialog chỉ return null — đừng tải chunk cho hàng trăm hạnh sinh.
    return dot ? (
        <Suspense fallback={null}>
            <CapNhatThongTinDialog />
        </Suspense>
    ) : null;
};

const CapNhatThongTinGate = () => {
    const { user } = useAuth();

    // Tài khoản không gắn mã hạnh sinh thì không nhắc: có điền cũng bị backend trả 400.
    if (!duocKeKhai(user)) return null;

    // Lỗi trong tính năng này KHÔNG được phép làm trắng màn hình toàn app.
    return (
        <FeatureErrorBoundary ten="cập nhật thông tin">
            <CapNhatThongTinGateHanhSinh maHs={user.maHs} />
        </FeatureErrorBoundary>
    );
};

export default CapNhatThongTinGate;
